const trapFocusHandlers = {};

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    setTimeout(() => {
      detailsElement.classList.add("menu-opening");
      summaryElement.setAttribute("aria-expanded", true);
      parentMenuElement && parentMenuElement.classList.add("submenu-open");
      !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
    }, 100);
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class AjaxLineItemInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    const ELM_lineItemVariation = this.querySelector(".variation");
    const ELM_lineItemProperties = this.querySelectorAll(".property");
    const ELM_selectedSize = [...ELM_lineItemProperties].find((elm) => elm.innerText.includes("Selected size: "));

    if (ELM_selectedSize?.innerText?.includes("Selected size: ")) {
      const text = ELM_selectedSize.innerText.split("Selected size: ")[1].trim();
      const rawText = ELM_lineItemVariation.innerText;
      const rawTextValue = rawText.split("Size: ")[1].trim();
      ELM_selectedSize.classList.add("d-none");
      if (rawTextValue !== text) {
        ELM_lineItemVariation.innerText = rawText + ` (${text})`;
      }
    }
  }
}

customElements.define("ajax-line-item-info", AjaxLineItemInfo);

// Gender Menu Toggle Handler
document.addEventListener("DOMContentLoaded", function () {
  const genderMenus = document.querySelectorAll(".mega-menu__link-wrapper--gender");

  genderMenus.forEach((genderMenu) => {
    const genderLinks = genderMenu.querySelectorAll('a[id^="HeaderMenu-gender-"]');
    const parentHeaderMenu = genderMenu.closest("header-menu");

    if (!genderLinks.length || !parentHeaderMenu) return;

    genderLinks.forEach((link) => {
      link.addEventListener("click", function (event) {
        event.preventDefault();

        const genderFilter = getGenderFromHref(this.getAttribute("href"));

        // Update active states
        updateActiveStates(genderLinks, this);

        // Update collection URLs
        updateCollectionUrls(parentHeaderMenu, genderFilter);
      });
    });
  });

  function getGenderFromHref(href) {
    if (href === "#men") return "mens";
    if (href === "#women") return "womens";
    return ""; // Default to 'all' (no filter)
  }

  function updateActiveStates(allGenderLinks, activeLink) {
    // Remove active class from all gender links
    allGenderLinks.forEach((link) => {
      link.classList.remove("mega-menu__link--active");
    });

    // Add active class to clicked link
    activeLink.classList.add("mega-menu__link--active");
  }

  function updateCollectionUrls(parentHeaderMenu, genderFilter) {
    // Find all collection links within the same header-menu
    const collectionLinks = parentHeaderMenu.querySelectorAll('a[href*="/collections/"]:not(.no-gender)');

    if (collectionLinks.length === 0) return;

    // Find the mega-menu list container
    const menuList = parentHeaderMenu.querySelector(".mega-menu__list");

    // Add animation class to trigger staggered animation
    if (menuList) {
      menuList.classList.add("updating-links");
    }

    // Update URLs immediately
    collectionLinks.forEach((link) => {
      const currentHref = link.getAttribute("href");
      const newHref = buildNewCollectionUrl(currentHref, genderFilter);

      if (newHref !== currentHref) {
        link.setAttribute("href", newHref);
      }
    });

    // Remove animation class after animation completes
    setTimeout(() => {
      if (menuList) {
        menuList.classList.remove("updating-links");
      }
    }, 800); // Max delay (450ms) + animation duration (300ms) + buffer
  }

  /* Filter Style */
  /* function buildNewCollectionUrl(currentUrl, genderFilter) {
    // Create URL object to easily manipulate query parameters
    const url = new URL(currentUrl, window.location.origin);

    // Remove existing gender filter
    url.searchParams.delete("filter.p.m.custom.gender");

    // Add new gender filter if not 'all'
    if (genderFilter && genderFilter !== "") {
      url.searchParams.set("filter.p.m.custom.gender", genderFilter);
    }

    // Return the full URL (pathname + search params)
    return url.pathname + url.search;
  } */

  /* Tag Style */
  function buildNewCollectionUrl(currentUrl, genderFilter) {
    // Remove existing gender filters from the URL
    let cleanUrl = currentUrl.replace(/\/(mens|womens)($|\?|#)/, "$2");

    // Add new gender filter if not 'all'
    if (genderFilter && genderFilter !== "") {
      // Check if URL already has query parameters or hash
      const hasParams = cleanUrl.includes("?") || cleanUrl.includes("#");
      const separator = hasParams ? (cleanUrl.includes("?") ? "" : "?") : "";

      // Insert gender before any existing query parameters or hash
      if (cleanUrl.includes("?")) {
        cleanUrl = cleanUrl.replace("?", `/${genderFilter}?`);
      } else if (cleanUrl.includes("#")) {
        cleanUrl = cleanUrl.replace("#", `/${genderFilter}#`);
      } else {
        cleanUrl = `${cleanUrl}/${genderFilter}`;
      }
    }

    return cleanUrl;
  }

  // Mobile Gender Menu Toggle Handler (Header Drawer)
  const mobileGenderMenus = document.querySelectorAll(".menu-item-gender__wrapper");

  mobileGenderMenus.forEach((genderWrapper) => {
    const genderLinks = genderWrapper.querySelectorAll('a[id^="HeaderDrawer-gender-"]');
    const drawerContainer = genderWrapper.closest("#menu-drawer");

    if (!genderLinks.length || !drawerContainer) return;

    genderLinks.forEach((link) => {
      link.addEventListener("click", function (event) {
        event.preventDefault();

        const genderFilter = getMobileGenderFromHref(this.getAttribute("href"));

        // Update active states
        updateMobileActiveStates(genderLinks, this);

        // Update collection URLs
        updateMobileCollectionUrls(drawerContainer, genderFilter);
      });
    });
  });

  function getMobileGenderFromHref(href) {
    if (href === "#men") return "mens";
    if (href === "#women") return "womens";
    return ""; // Default to 'all' (no filter)
  }

  function updateMobileActiveStates(allGenderLinks, activeLink) {
    // Remove active class from all gender links
    allGenderLinks.forEach((link) => {
      link.classList.remove("active");
    });

    // Add active class to clicked link
    activeLink.classList.add("active");
  }

  function updateMobileCollectionUrls(drawerContainer, genderFilter) {
    // Find all collection links within the entire drawer, including nested submenus
    const collectionLinks = drawerContainer.querySelectorAll('a[href*="/collections/"]:not(.no-gender)');

    if (collectionLinks.length === 0) return;

    // Find all menu containers for staggered animation
    const menuContainers = drawerContainer.querySelectorAll(".menu-drawer__menu");

    // Add animation class to trigger staggered animation
    menuContainers.forEach((menuContainer) => {
      menuContainer.classList.add("updating-links");
    });

    // Update URLs immediately
    collectionLinks.forEach((link) => {
      const currentHref = link.getAttribute("href");
      const newHref = buildNewCollectionUrl(currentHref, genderFilter);

      if (newHref !== currentHref) {
        link.setAttribute("href", newHref);
      }
    });

    // Remove animation class after animation completes
    setTimeout(() => {
      menuContainers.forEach((menuContainer) => {
        menuContainer.classList.remove("updating-links");
      });
    }, 800); // Max delay (450ms) + animation duration (300ms) + buffer
  }
});
